<script setup>
import { computed, ref, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { isVisible } from "common/utils/ui/element";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import DeliveryDateTimeleft from "./DeliveryDateTimeleft.vue";
import { trackZipcodeLocationChange } from "../utils/tracker";

const props = defineProps({
  placement: {
    type: String,
    default: "checkout",
    validator: value => ["landing", "checkout"].includes(value)
  }
});
const store = useStore();
const zipcodeInput = ref(null);
const deliveryDate = computed(() => store.getters["delivery/deliveryDate"]);
const error = computed(() => store.getters["delivery/error"]);
const isLoading = computed(() => store.getters["loading/delivery"]);
const zipcode = computed(() => store.getters["delivery/zipcode"]);
const zipcodeSaved = computed(() => store.getters["delivery/zipcodeSaved"]);
const getDeliveryDate = () => store.dispatch("delivery/getDelivery");
const zipcodeModel = ref(zipcode.value);

const handleZipcode = () => {
  const oldZipcode = zipcode.value;
  const newZipcode = zipcodeModel.value;

  if (isLoading.value || !newZipcode || oldZipcode === newZipcode) return;
  store.dispatch("delivery/updateZipcode", newZipcode);

  trackZipcodeLocationChange(oldZipcode, newZipcode);
};

const isLandingStyle = computed(() => props.placement === "landing");

watch(zipcodeModel, (zipcodeVal, prevZipcodeVal) => {
  if (zipcodeVal?.length === 5 && prevZipcodeVal?.length !== 5) {
    handleZipcode();
  }
});

watch(zipcode, (zipcodeVal, prevZipcodeVal) => {
  if (zipcodeVal !== prevZipcodeVal) {
    zipcodeModel.value = zipcode.value;
  }
});

onMounted(() => {
  if (!isVisible(zipcodeInput.value) || zipcodeSaved.value) return;

  store.dispatch("delivery/updateZipcode", zipcode.value);
});
</script>

<template>
  <div ref="zipcodeInput" class="zipcode-input" :class="`zipcode-input--${placement}`">
    <h3 v-if="!isLandingStyle" class="zipcode-input__title">
      <UseSvg
        class="zipcode-input__title-icon zipcode-input__title-icon--big"
        icon="zap"
        type="print"
      />
      <UseSvg
        class="zipcode-input__title-icon zipcode-input__title-icon--small"
        icon="zap"
        type="print"
      />
      <span class="zipcode-input__title-text">Delivering super fast to</span>
    </h3>

    <div class="zipcode-input__fieldset">
      <div class="zipcode-input__field-wrap">
        <input
          v-model="zipcodeModel"
          class="zipcode-input__field"
          :class="{ 'zipcode-input__field--error': error }"
          maxlength="5"
          type="tel"
          @blur="handleZipcode"
          @keydown.enter.prevent.stop="handleZipcode"
        />
        <label class="zipcode-input__label">
          ZIP <br v-if="isLandingStyle" />
          Code
        </label>
      </div>

      <div class="zipcode-input__delivery-date">
        <!-- start: delivery content switch -->
        <div
          v-if="isLoading"
          class="zipcode-input__delivery-date-content zipcode-input__delivery-date-content--loading"
        />
        <div
          v-else-if="error"
          class="zipcode-input__delivery-date-content zipcode-input__delivery-date-content--error"
        >
          {{ error }}
        </div>
        <div
          v-else-if="!zipcode"
          class="zipcode-input__delivery-date-content zipcode-input__delivery-date-content--empty"
        >
          <span class="zipcode-input__delivery-date-text">Enter zip code first..</span>
        </div>
        <div
          v-else-if="deliveryDate.full"
          class="zipcode-input__delivery-date-content zipcode-input__delivery-date-content--date"
        >
          <UseSvg class="zipcode-input__delivery-date-icon" icon="delivery" type="print" />
          <div>
            <span class="zipcode-input__delivery-date-text">{{ deliveryDate.full }}</span>

            <div
              v-if="deliveryDate.timeleft && !error && isLandingStyle"
              class="zipcode-input__timeleft"
            >
              <DeliveryDateTimeleft
                :deadline="deliveryDate.timeleft"
                :on-timeout="getDeliveryDate"
                short
              />
            </div>
          </div>
        </div>
        <!-- end: delivery content switch -->

        <div
          v-if="deliveryDate.timeleft && !error && !isLandingStyle"
          class="zipcode-input__timeleft"
        >
          <DeliveryDateTimeleft :deadline="deliveryDate.timeleft" :on-timeout="getDeliveryDate" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin.scss";
@import "frontend/init/pod/settings";

.zipcode-input {
  $this: &;

  text-align: center;

  &__title {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: center;
    position: relative;
    margin-bottom: 12px;
    font-size: 15px;
    font-weight: 500;
  }

  &__title-icon {
    display: inline-block;
    color: $pod-premium-ligh;

    &--big {
      width: 15px;
      height: 24px;
    }

    &--small {
      position: relative;
      top: 5px;
      right: 3px;
      width: 9px;
      height: 13px;
      opacity: 0.4;
    }
  }

  &__title-text {
    padding-left: 7px;
  }

  &__fieldset {
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }

  &__field-wrap {
    flex-shrink: 0;
    width: 80px;
  }

  &__field {
    width: 100%;
    height: 52px;
    border: 2px solid $pod-geyser;
    border-radius: 6px;
    text-align: inherit;

    &--error {
      border-color: $pod-danger;
      outline-color: $pod-danger;
    }
  }

  &__label {
    display: block;
    color: $pod-gray-modern-700;
    font-size: 11px;
    font-weight: 600;
    line-height: 20px;
  }

  &__delivery-date {
    flex-grow: 1;
  }

  &__delivery-date-content {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    height: 52px;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 500;

    &--empty {
      background-color: $pod-gray-modern-100;
      color: $pod-gray-modern-900;
    }

    &--error {
      font-size: 11px;
      background-color: $pod-danger-light;
      color: $red-error;
    }

    &--loading {
      @include vlc-animation;

      background-color: $pod-gray-modern-100;
    }

    &--date {
      background: linear-gradient(90deg, $pod-fog-light 0%, $pod-fog 100%);
      color: $pod-gray-modern-900;
      font-size: 17px;
      font-weight: 600;
    }
  }

  &__delivery-date-icon {
    width: 28px;
    height: 23px;
    margin-right: 7px;
  }

  &__error {
    color: $red-error;
    font-size: 12px;
  }

  &__timeleft {
    padding-top: 1px;
  }

  &--landing {
    #{$this}__field-wrap {
      display: flex;
      flex-direction: row-reverse;
      width: auto;
      gap: 12px;
      align-items: center;
    }

    #{$this}__field {
      width: 81px;
      height: 40px;
      border: 0;
      font-size: 14px;
      font-weight: 600;
      border-radius: 4px;
      color: $pod-gray-modern-900;
    }

    #{$this}__fieldset {
      @include with-tablet {
        gap: 24px;
      }
    }

    #{$this}__label {
      color: $white;
      font-size: 14px;
      font-weight: 600;
      text-align: right;
    }

    #{$this}__delivery-date {
      width: 250px;
    }

    #{$this}__delivery-date-content {
      justify-content: flex-end;
    }

    #{$this}__delivery-date-content--date {
      background: none;
      color: $white;
    }

    #{$this}__delivery-date-content--loading {
      background: $pod-date-blue-bg;
    }

    #{$this}__delivery-date-icon {
      margin-right: 9px;
    }

    :deep(.delivery-date-time-left) {
      text-align: left;

      @include with-tablet {
        font-size: 13px;
        font-weight: 400;
      }
    }

    :deep(.delivery-date-time-left__timer) {
      color: $pod-date-orange-bg;
    }
  }
}
</style>
